import { useQuery, useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { EntityType, CommentOrderingField } from "../types/api/comments";
import {
  fetchComment,
  fetchComments,
  fetchCommentsAsTree,
  deleteComment,
} from "../services/comments";

// Постраничный хук для комментариев
export const useCommentsQuery = (
  entityId: string,
  entityType: EntityType,
  is_admin: boolean,
  page: number,
  limit: number,
  created_from?: string,
  created_to?: string,
  search?: string,
  user_id?: string,
  ordering?: CommentOrderingField
) => {
  return useQuery({
    refetchOnWindowFocus: false,
    queryKey: [
      "comments",
      entityId,
      entityType,
      is_admin,
      page,
      limit,
      created_from,
      created_to,
      search,
      user_id,
      ordering,
    ],
    queryFn: () =>
      fetchComments({
        entity_id: entityId,
        entity_type: entityType,
        is_admin,
        limit,
        offset: (page - 1) * limit,
        created_from,
        created_to,
        search,
        user_id,
        ordering,
      }),
    retry: 0,
    enabled: !!entityId && !!entityType,
  });
};

// Хук для древовидного списка комментариев
export const useCommentsTreeQuery = (
  entityId: string,
  entityType: EntityType,
  is_admin: boolean,
  page: number,
  limit: number,
  created_from?: string,
  created_to?: string,
  search?: string,
  user_id?: string,
  ordering?: CommentOrderingField
) => {
  return useQuery({
    refetchOnWindowFocus: false,
    queryKey: [
      "comments-tree",
      entityId,
      entityType,
      is_admin,
      page,
      limit,
      created_from,
      created_to,
      search,
      user_id,
      ordering,
    ],
    queryFn: () =>
      fetchCommentsAsTree({
        entity_id: entityId,
        entity_type: entityType,
        is_admin,
        limit,
        offset: (page - 1) * limit,
        created_from,
        created_to,
        search,
        user_id,
        ordering,
      }),
    retry: 0,
    enabled: !!entityId && !!entityType,
  });
};

// Хук для бесконечной прокрутки комментариев
export const useCommentsInfiniteQuery = (
  entityId: string,
  entityType: EntityType,
  is_admin: boolean,
  limit: number,
  created_from?: string,
  created_to?: string,
  search?: string,
  user_id?: string,
  ordering?: CommentOrderingField,
  options?: {
    enabled?: boolean;
  }
) => {
  return useInfiniteQuery({
    queryKey: [
      "comments-infinite",
      entityId,
      entityType,
      is_admin,
      limit,
      created_from,
      created_to,
      search,
      user_id,
      ordering,
    ],
    queryFn: ({ pageParam = { limit: limit, offset: 0 } }) =>
      fetchComments({
        entity_id: entityId,
        entity_type: entityType,
        is_admin,
        limit: pageParam.limit,
        offset: pageParam.offset,
        created_from,
        created_to,
        search,
        user_id,
        ordering,
      }),
    initialPageParam: { limit: limit, offset: 0 },
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce(
        (acc, page) => acc + page.list.length,
        0
      );
      const hasMore = lastPage.list.length === limit;
      return hasMore ? { limit: limit, offset: totalLoaded } : undefined;
    },
    retry: 0,
    enabled: (options?.enabled ?? true) && !!entityId && !!entityType,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

// Хук для бесконечной прокрутки древовидного списка комментариев
export const useCommentsTreeInfiniteQuery = (
  entityId: string,
  entityType: EntityType,
  is_admin: boolean,
  limit: number,
  created_from?: string,
  created_to?: string,
  search?: string,
  user_id?: string,
  ordering?: CommentOrderingField,
  options?: {
    enabled?: boolean;
  }
) => {
  return useInfiniteQuery({
    queryKey: [
      "comments-tree-infinite",
      entityId,
      entityType,
      is_admin,
      limit,
      created_from,
      created_to,
      search,
      user_id,
      ordering,
    ],
    queryFn: ({ pageParam = { limit: limit, offset: 0 } }) =>
      fetchCommentsAsTree({
        entity_id: entityId,
        entity_type: entityType,
        is_admin,
        limit: pageParam.limit,
        offset: pageParam.offset,
        created_from,
        created_to,
        search,
        user_id,
        ordering,
      }),
    initialPageParam: { limit: limit, offset: 0 },
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce(
        (acc, page) => acc + page.list.length,
        0
      );
      const hasMore = lastPage.list.length === limit;
      return hasMore ? { limit: limit, offset: totalLoaded } : undefined;
    },
    retry: 0,
    enabled: (options?.enabled ?? true) && !!entityId && !!entityType,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

// Хук для одного комментария
export const useCommentQuery = (id: string, enabled = true) => {
  return useQuery({
    queryKey: ["comment", id],
    queryFn: () => fetchComment(id),
    enabled: enabled && !!id,
    refetchOnWindowFocus: false,
    retry: 0,
  });
};

export const useDeleteCommentMutation = () => {
  return useMutation({
    mutationFn: (uuids: string[]) => deleteComment(uuids),
    retry: 0,
  });
};
